import styled from '@emotion/styled';
import { useIsMobile, useIsTablet } from '@hooks/responsive/useIsMobile';

// components
import Divider from '@components/_atoms/Divider';
import Typography from '@components/_atoms/Typography';

// constants
import { TypoVariant } from '@constants/atoms';
import { LAPTOP_MODE, MOBILE_MODE, TABLET_MODE } from '@constants/size';
import { gray, primary } from '@styles/Colors';
import { useTranslation } from 'next-i18next';

const Content = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 40px;

  @media ${TABLET_MODE} {
    max-width: 768px;
  }

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 0;
  }
`;

const DescriptionBox = styled.div`
  display: grid;
  row-gap: 16px;
`;

const MissionList = styled.div`
  margin-top: 80px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 24px;

  @media ${LAPTOP_MODE} {
    margin-top: 40px;
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${MOBILE_MODE} {
    grid-template-columns: none;
    row-gap: 40px;
  }
`;

const MissionItem = styled.div<{ ko: boolean }>`
  display: grid;
  row-gap: 8px;

  ${({ ko }) =>
    !ko &&
    `
    display:flex;
    flex-direction:column;
    gap: 8px;
  `}
`;

const HighLightBlue = styled.span`
  color: ${primary.blue};
`;

export default function Section0() {
  const { t, i18n } = useTranslation('intro');
  const ko = i18n.language === 'ko';

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  return (
    <Content>
      <DescriptionBox>
        <Typography
          variant={isMobile ? TypoVariant.SH4 : isTablet ? TypoVariant.SH3 : TypoVariant.SH2}
          color={primary.blue}>
          Our Mission & Core Value
        </Typography>
        <Typography
          variant={isMobile ? TypoVariant.SH2 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
          color={primary.gray}>
          {t('section0.title')}
        </Typography>
      </DescriptionBox>

      <MissionList>
        <MissionItem ko={ko}>
          <Typography
            variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
            color={gray.gray4}>
            01.
          </Typography>
          <Divider />
          {ko ? (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              우리는
              <br />
              밀도 높은 <HighLightBlue>몰입</HighLightBlue>과
            </Typography>
          ) : (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              We continuously
              <br />
              push ourselves forward
            </Typography>
          )}
        </MissionItem>

        <MissionItem ko={ko}>
          <Typography
            variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
            color={gray.gray4}>
            02.
          </Typography>
          <Divider />
          {ko ? (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              논리적인
              <br />
              <HighLightBlue>소통</HighLightBlue>을 통한
            </Typography>
          ) : (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              driven by
              <br />
              total immersion
            </Typography>
          )}
        </MissionItem>

        <MissionItem ko={ko}>
          <Typography
            variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
            color={gray.gray4}>
            03.
          </Typography>
          <Divider />
          {ko ? (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              서로의
              <br />
              <HighLightBlue>신뢰</HighLightBlue>를 바탕으로
            </Typography>
          ) : (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              {/* <></>
              <br /> */}
              logical communication
            </Typography>
          )}
        </MissionItem>

        <MissionItem ko={ko}>
          <Typography
            variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}
            color={gray.gray4}>
            04.
          </Typography>
          <Divider />
          {ko ? (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              끝없이
              <br /> <HighLightBlue>도전</HighLightBlue>합니다.
            </Typography>
          ) : (
            <Typography variant={isMobile ? TypoVariant.SH1 : isTablet ? TypoVariant.H6 : TypoVariant.H5}>
              and
              <br />
              mutual trust
            </Typography>
          )}
        </MissionItem>
      </MissionList>
    </Content>
  );
}
